import * as styles from 'common/components/GenericModal/styles';
import { Box } from '@mui/material';
import { DndItem, DraggableOccasionCard } from 'containers/DndStackContainer/types';
import { DndItemWrapper } from 'containers/DndStackContainer/components/DndItemWrapper';
import { DndOccasionCardFieldType } from 'containers/HomePageContainer/components/PageConfigurator/ShopByOccasionModalContent/DndOccasionCard/field-type.enum';
import { Form, Formik } from 'formik';
import { FormikErrorsListener } from 'common/components/FormikErrorsListener';
import { FormikItemSubmitFormInitializer } from 'containers/DndStackContainer/utils/FormikSubmitFormInitializer';
import { LocalizedTextField } from 'containers/HomePageContainer/utils/LocalizedTextField';
import { PageType } from 'common/enums/page-type.enum';
import { TextField } from 'common/components/TextField';
import { WidgetLayout } from 'common/enums/widget-layout.enum';
import { dndOccasionCardSchema } from 'containers/HomePageContainer/components/PageConfigurator/ShopByOccasionModalContent/DndOccasionCard/dnd-banner-card.schema';
import { isEmptyOrUndefined } from 'utils/string/is-empty-or-undefined';
import React, {
  FC, FocusEvent, useCallback, useEffect, useState,
} from 'react';

export interface DndOccasionCardProps extends DraggableOccasionCard {
  isDragging: boolean;
  deleteItem: (id: string) => void;
  updateItemState: (
    id: string,
    state: DndItem
  ) => void;
  pageType: PageType;
}

export interface OccasionCardFormData {
  name: string;
  frName?: string;
  image: string;
  frImage?: string;
  link: string;
}

const defaultInitialValues: OccasionCardFormData = {
  name: '',
  frName: '',
  image: '',
  frImage: '',
  link: '',
};

export interface HandleUpdateItemProps {
  itemId: string;
  type: DndOccasionCardFieldType;
  value: string | boolean | (() => Promise<void>) | string[];
}

export const DndOccasionCard: FC<DndOccasionCardProps> = ({
  id, name, frName, image, frImage, link, updateItemState: updateItem,
  isDragging, deleteItem, layout, isValid: isInitiallyValid,
  onSubmitCallback, sources,
  pageType,
}) => {
  const [isValid, setIsValid] = useState(Boolean(isInitiallyValid));
  const [nameTextLanguage, setNameTextLanguage] = useState('en');
  const [imageTextLanguage, setImageTextLanguage] = useState('en');
  const areInitialFieldValues = isEmptyOrUndefined(name)
    || isEmptyOrUndefined(frName)
    || isEmptyOrUndefined(image)
    || isEmptyOrUndefined(frImage)
    || isEmptyOrUndefined(link);

  const handleUpdateItem = ({
    itemId, type, value,
  }: HandleUpdateItemProps): void => {
    if (!updateItem) {
      return;
    }
    const newState = {
      name, frName, image, frImage, link, layout, isValid, onSubmitCallback, sources,
    };
    if (type === DndOccasionCardFieldType.Name) {
      updateItem(itemId, { ...newState, name: String(value) });
      return;
    }
    if (type === DndOccasionCardFieldType.FrName) {
      updateItem(itemId, { ...newState, frName: String(value) });
      return;
    }
    if (type === DndOccasionCardFieldType.Image) {
      updateItem(itemId, { ...newState, image: String(value) });
      return;
    }
    if (type === DndOccasionCardFieldType.FrImage) {
      updateItem(itemId, { ...newState, frImage: String(value) });
      return;
    }
    if (type === DndOccasionCardFieldType.IsValid) {
      updateItem(itemId, { ...newState, isValid: Boolean(value) });
      return;
    }
    if (type === DndOccasionCardFieldType.SubmitFormCallback) {
      updateItem(itemId, { ...newState, onSubmitCallback: value as (() => Promise<void>) });
    }
    if (type === DndOccasionCardFieldType.Link) {
      updateItem(itemId, { ...newState, link: String(value) });
    }
    if (type === DndOccasionCardFieldType.Sources) {
      updateItem(itemId, { ...newState, sources: value as string[] });
    }
  };

  useEffect(() => {
    if (areInitialFieldValues) {
      handleUpdateItem({ itemId: id!, type: DndOccasionCardFieldType.IsValid, value: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!areInitialFieldValues) {
      handleUpdateItem({ itemId: id!, type: DndOccasionCardFieldType.IsValid, value: isValid });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const onBlur = (fieldType: DndOccasionCardFieldType) => (
    { target: { value } }: FocusEvent<HTMLInputElement>,
  ) => {
    handleUpdateItem({ itemId: id!, type: fieldType, value: String(value) });
  };

  const setOnSubmitFormForItemCallback = useCallback((callback: () => Promise<void>) => {
    setTimeout(() => {
      handleUpdateItem({
        itemId: id!, type: DndOccasionCardFieldType.SubmitFormCallback, value: callback,
      });
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DndItemWrapper
      layout={layout ?? WidgetLayout.Common}
      isDragging={isDragging}
      isActive
      deleteItem={deleteItem}
      id={id!}
    >
      <Formik
        initialValues={{
          ...(id
            ? {
              id, name, frName, image, frImage, link, isValid,
            }
            : defaultInitialValues),
          pageType,
        }}
        validationSchema={dndOccasionCardSchema}
        onSubmit={() => {}}
      >
        {({ submitForm, errors, setFieldValue }) => (
          <Form>
            <Box p={2}>
              <FormikItemSubmitFormInitializer
                submitForm={submitForm}
                setOnSubmitFormForItem={setOnSubmitFormForItemCallback}
              />
              <FormikErrorsListener
                onErrors={() => setIsValid(false)}
                onNoErrors={() => setIsValid(true)}
              />
              <LocalizedTextField
                label="Occasion Name"
                englishText={name || ''}
                frenchText={frName || ''}
                textLanguage={nameTextLanguage}
                setTextLanguage={setNameTextLanguage}
                onBlur={(event) => {
                  if (nameTextLanguage === 'en') {
                    setFieldValue('name', event.target.value);
                    handleUpdateItem({
                      itemId: id!,
                      type: DndOccasionCardFieldType.Name,
                      value: event.target.value,
                    });
                  } else {
                    handleUpdateItem({
                      itemId: id!,
                      type: DndOccasionCardFieldType.FrName,
                      value: event.target.value,
                    });
                  }
                }}
                errors={errors?.name}
              />
              <LocalizedTextField
                label="Occasion Image"
                englishText={image || ''}
                frenchText={frImage || ''}
                textLanguage={imageTextLanguage}
                setTextLanguage={setImageTextLanguage}
                onBlur={(event) => {
                  if (imageTextLanguage === 'en') {
                    setFieldValue('image', event.target.value);
                    handleUpdateItem({
                      itemId: id!,
                      type: DndOccasionCardFieldType.Image,
                      value: event.target.value,
                    });
                  } else {
                    handleUpdateItem({
                      itemId: id!,
                      type: DndOccasionCardFieldType.FrImage,
                      value: event.target.value,
                    });
                  }
                }}
                errors={errors?.image}
              />
              <TextField
                label="Occasion Link"
                name="link"
                variant="standard"
                sx={styles.textField}
                fullWidth
                onBlur={onBlur(DndOccasionCardFieldType.Link)}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </DndItemWrapper>
  );
};
